// OWNER: FRONTEND-ADMIN. Settings → Integrations (admin): booked meetings to Discord (owner request, PJ, 2026-10-06;
// migration 0027). The webhook URL is a Worker secret (never shown); these switches turn the post and the attachment on/off.
// "Connected" means Discord really accepted a post or a test post for the current URL (discordWebhookHealth); a webhook
// Discord says is gone shows as failing, and every post waits until the URL is fixed.
import { useState } from 'preact/hooks';
import { errorMessage } from '../api';
import { ErrorBanner, fmtDateTime, toast } from '../components/ui';
import { adminApi, type AdminSettings } from './endpoints';
import { Check } from './ui';

export function IntegrationsAdmin(props: { settings: AdminSettings; onSaved: (s: AdminSettings) => void }) {
  const s = props.settings;
  const [enabled, setEnabled] = useState(s.discordMeetingsEnabled ?? true);
  const [attach, setAttach] = useState(s.discordAttachAudio ?? true);
  const [busy, setBusy] = useState<'save' | 'test' | 'retry' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  if (s.discordWebhook === undefined) {
    return <div class="adm-muted-box">This server doesn't have the Discord integration yet (migration 0027 and the booked-calls code).</div>;
  }
  const dirty = enabled !== s.discordMeetingsEnabled || attach !== s.discordAttachAudio;
  const save = async () => {
    setBusy('save'); setError(null);
    try {
      const next = await adminApi.patchSettings({
        ...(enabled !== s.discordMeetingsEnabled ? { discordMeetingsEnabled: enabled } : {}),
        ...(attach !== s.discordAttachAudio ? { discordAttachAudio: attach } : {}),
      });
      props.onSaved(next);
      toast('Integrations saved.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy('test'); setError(null); setTestResult(null);
    try {
      const r = await adminApi.discordTest();
      setTestResult({ ok: r.ok, message: r.message });
      props.onSaved(r.settings);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  };
  const retry = async () => {
    setBusy('retry'); setError(null);
    try {
      const r = await adminApi.discordRetryFailed();
      props.onSaved(r.settings);
      toast(r.reset ? `${r.reset} post${r.reset === 1 ? '' : 's'} will be sent again within a few minutes.` : 'No failed posts to send again.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  };
  const hook = s.discordWebhook;
  const health = s.discordWebhookHealth ?? { state: 'untested' as const, at: null, error: null };
  const failed = s.discordFailedPosts ?? { failing: 0, gaveUp: 0 };
  const failedTotal = failed.failing + failed.gaveUp;
  return (
    <div class="stack" style="max-width:760px">
      <div class="card stack">
        <h3>Discord: booked meetings</h3>
        <div class="adm-sub">
          Every booked meeting is posted once to the team's channel: who booked whom and when, the closer brief, a link to the meeting in the
          dialer, and the call recording as a file (under 8 MB; longer calls get the link only). Prospects' phone numbers and email
          addresses are never posted. Posts start after the brief is ready, or 30 minutes after the call with no recording.
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center">
          <span>Webhook:</span>
          {hook === 'invalid' ? <span class="chip chip-bad">Not a Discord webhook URL</span>
            : hook === 'missing' ? <span class="chip chip-warn">Not connected</span>
              : health.state === 'ok' ? <span class="chip chip-ok" title={health.at ? `Discord accepted a post ${fmtDateTime(health.at)}` : undefined}>Connected</span>
                : health.state === 'failing' ? <span class="chip chip-bad">Failing: posts paused</span>
                  : <span class="chip chip-warn">Set, not tested yet</span>}
          {hook === 'set' ? (
            <button class="btn btn-sm" disabled={busy !== null} onClick={test}>{busy === 'test' ? 'Sending…' : 'Send test post'}</button>
          ) : null}
        </div>
        {hook === 'set' && health.state === 'ok' && health.at ? (
          <div class="adm-sub">Discord last accepted a post {fmtDateTime(health.at)}.</div>
        ) : null}
        {hook === 'set' && health.state === 'untested' ? (
          <div class="adm-sub">The URL has the right shape, but Discord hasn't accepted a post from it yet. Send a test post to be sure.</div>
        ) : null}
        {hook === 'set' && health.state === 'failing' ? (
          <div style="font-size:14px;color:var(--red-ink)">
            {health.error}{health.at ? ` (${fmtDateTime(health.at)})` : ''} Every post is paused; nothing is lost. Make a new webhook in Discord and
            run <code>npx wrangler secret put DISCORD_MEETINGS_WEBHOOK_URL</code> (a new URL restarts posting by itself), or press Send test post
            once the webhook works again.
          </div>
        ) : null}
        {testResult ? <div class={testResult.ok ? 'adm-sub' : ''} style={testResult.ok ? undefined : 'font-size:14px;color:var(--red-ink)'}>{testResult.message}</div> : null}
        {hook !== 'set' ? (
          <div class="adm-sub">
            In Discord: the channel's settings → Integrations → Webhooks → New Webhook → Copy Webhook URL. Then, from <code>ai-agency/dialer</code>:{' '}
            <code>npx wrangler secret put DISCORD_MEETINGS_WEBHOOK_URL</code> and paste it. The URL is the password to the channel: it is never shown here.
          </div>
        ) : null}
        <Check checked={enabled} onChange={setEnabled} label="Post each booked meeting to Discord" />
        <Check checked={attach} onChange={setAttach} disabled={!enabled} label="Attach the call recording to the post (under 8 MB)" />
        {failedTotal ? (
          <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center">
            <span class="adm-sub" style="margin:0">
              {failedTotal} booked meeting{failedTotal === 1 ? '' : 's'} not posted after a failure
              {failed.gaveUp ? ` (${failed.gaveUp} gave up after 4 tries)` : ''}.
            </span>
            <button class="btn btn-sm" disabled={busy !== null || health.state === 'failing'} title={health.state === 'failing' ? 'Fix the webhook first.' : undefined} onClick={retry}>
              {busy === 'retry' ? 'Retrying…' : 'Retry failed posts'}
            </button>
          </div>
        ) : null}
        <div class="adm-sub">
          Meetings booked in the last 3 days are posted automatically once the webhook is connected. Older ones: run{' '}
          <code>node tools/booked_calls_backfill.mjs</code> (dry run), then add <code>--live</code>.
        </div>
        <ErrorBanner error={error} />
        <div class="row">
          <button class="btn btn-primary" disabled={!dirty || busy !== null} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
          <button class="btn" disabled={!dirty || busy !== null} onClick={() => { setEnabled(s.discordMeetingsEnabled ?? true); setAttach(s.discordAttachAudio ?? true); }}>Revert</button>
        </div>
      </div>
    </div>
  );
}
